import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { ChevronDownIcon, CloseIcon, MinimizeIcon } from '#/shared/ui/icons'
import {
  TooltipContent,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
} from '#/shared/ui/tooltip'
import { UserDropdown } from '#/shared/ui/user-dropdown'
import type { UserOption } from '#/shared/ui/user-dropdown'

import { ArchiveReasonDropdown } from './archive-reason-dropdown'
import { MoreMenuDropdown } from './more-menu-dropdown'
import type {
  ArchiveSubmenuConfig,
  ExtraMenuItem,
  MarkAsConfig,
  MoreMenuAction,
} from './more-menu-dropdown'
import { PostToLBDropdown } from './post-to-lb-dropdown'

interface DetailToolbarProps {
  id: string
  status?: string
  user: UserOption
  users?: UserOption[]
  onUserChange?: (user: UserOption) => void
  onMinimize?: () => void
  onClose?: () => void
  onPrev?: () => void
  onNext?: () => void
  onMoreAction?: (action: MoreMenuAction) => void
  extraMenuItems?: ExtraMenuItem[]
  markAs?: MarkAsConfig
  backToQuote?: MarkAsConfig
  archive?: ArchiveSubmenuConfig
  primaryAction?: React.ReactNode
  requests?: React.ReactNode
}

function IconBoxButton({
  label,
  onClick,
  children,
}: {
  label: string
  onClick?: () => void
  children: React.ReactNode
}) {
  const disabled = onClick == null
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'group flex size-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 transition-colors duration-150',
        disabled
          ? 'cursor-not-allowed opacity-40'
          : 'cursor-pointer hover:bg-gray-50 hover:text-gray-900',
      )}
    >
      {children}
    </button>
  )
}

function CopyIdButton({
  id,
  hintLabel,
  copiedLabel,
}: {
  id: string
  hintLabel: string
  copiedLabel: string
}) {
  const [open, setOpen] = React.useState(false)
  const [copied, setCopied] = React.useState(false)
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  React.useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    },
    [],
  )

  const handleCopy = async () => {
    if (!id) return
    try {
      await navigator.clipboard.writeText(id)
    } catch {
      return
    }
    setCopied(true)
    setOpen(true)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setCopied(false), 2000)
  }

  return (
    <TooltipProvider delayDuration={200}>
      <TooltipRoot open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={handleCopy}
            disabled={!id}
            className="cursor-pointer text-base text-primary transition-colors hover:underline disabled:cursor-default disabled:opacity-60 disabled:hover:no-underline"
          >
            {id}
          </button>
        </TooltipTrigger>
        <TooltipContent>{copied ? copiedLabel : hintLabel}</TooltipContent>
      </TooltipRoot>
    </TooltipProvider>
  )
}

function ToolbarLeft({
  id,
  status,
  onMinimize,
  onClose,
  onPrev,
  onNext,
  minimizeLabel,
  closeLabel,
  prevLabel,
  nextLabel,
  copyHint,
  copiedLabel,
  requests,
}: {
  id: string
  status?: string
  onMinimize?: () => void
  onClose?: () => void
  onPrev?: () => void
  onNext?: () => void
  minimizeLabel: string
  closeLabel: string
  prevLabel: string
  nextLabel: string
  copyHint: string
  copiedLabel: string
  requests?: React.ReactNode
}) {
  return (
    <div className="flex w-full items-center gap-3.75 md:h-full md:w-auto">
      <div className="flex items-center gap-1.25">
        <IconBoxButton label={closeLabel} onClick={onClose}>
          <CloseIcon className="size-3.5 icon-hover" />
        </IconBoxButton>
        <IconBoxButton label={prevLabel} onClick={onPrev}>
          <ChevronDownIcon className="size-3.5 rotate-180 icon-hover" />
        </IconBoxButton>
        <IconBoxButton label={nextLabel} onClick={onNext}>
          <ChevronDownIcon className="size-3.5 icon-hover" />
        </IconBoxButton>
        {onMinimize && (
          <IconBoxButton label={minimizeLabel} onClick={onMinimize}>
            <MinimizeIcon className="size-4 icon-hover" />
          </IconBoxButton>
        )}
      </div>
      <CopyIdButton id={id} hintLabel={copyHint} copiedLabel={copiedLabel} />
      {status && (
        <span className="flex h-6 items-center rounded-md bg-gray-200 px-1.25 py-0.75 text-sm text-gray-700">
          {status}
        </span>
      )}
      {requests && <div className="flex-1 md:flex-none">{requests}</div>}
    </div>
  )
}

function ToolbarRight({
  user,
  users,
  onUserChange,
  onMoreAction,
  extraMenuItems,
  markAs,
  backToQuote,
  archive,
  postLabel,
  moreLabel,
  primaryAction,
}: {
  user: UserOption
  users?: UserOption[]
  onUserChange?: (u: UserOption) => void
  onMoreAction?: (action: MoreMenuAction) => void
  extraMenuItems?: ExtraMenuItem[]
  markAs?: MarkAsConfig
  backToQuote?: MarkAsConfig
  archive?: ArchiveSubmenuConfig
  postLabel: string
  moreLabel: string
  primaryAction?: React.ReactNode
}) {
  return (
    <div className="flex h-8 w-full items-center gap-2.5 md:w-auto">
      <UserDropdown
        currentUser={user}
        users={users ?? [user]}
        onSelect={onUserChange}
      />
      {primaryAction ?? <PostToLBDropdown label={postLabel} />}
      {archive && (
        <div className="hidden md:block">
          <ArchiveReasonDropdown
            label={archive.label}
            stage={archive.stage}
            shipmentId={archive.shipmentId}
            onArchived={archive.onArchived}
          />
        </div>
      )}
      <MoreMenuDropdown
        label={moreLabel}
        onSelect={onMoreAction}
        extraItems={extraMenuItems}
        markAs={markAs}
        backToQuote={backToQuote}
        archive={archive}
      />
    </div>
  )
}

export function DetailToolbar({
  id,
  status,
  user,
  users,
  onUserChange,
  onMinimize,
  onClose,
  onPrev,
  onNext,
  onMoreAction,
  extraMenuItems,
  markAs,
  backToQuote,
  archive,
  primaryAction,
  requests,
}: DetailToolbarProps) {
  const { t } = useTranslation()
  return (
    <div className="flex w-full flex-col gap-2.5 py-1.5 md:flex-row md:items-center md:justify-between md:gap-0">
      <ToolbarLeft
        id={id}
        status={status}
        onMinimize={onMinimize}
        onClose={onClose}
        onPrev={onPrev}
        onNext={onNext}
        minimizeLabel={t('detailToolbar.minimize')}
        closeLabel={t('detailToolbar.close')}
        prevLabel={t('detailToolbar.previous')}
        nextLabel={t('detailToolbar.next')}
        copyHint={t('detailToolbar.copyHint')}
        copiedLabel={t('detailToolbar.idCopied', { id })}
        requests={requests}
      />
      <ToolbarRight
        user={user}
        users={users}
        onUserChange={onUserChange}
        onMoreAction={onMoreAction}
        extraMenuItems={extraMenuItems}
        markAs={markAs}
        backToQuote={backToQuote}
        archive={archive}
        postLabel={t('detailToolbar.postToLB')}
        moreLabel={t('detailToolbar.more')}
        primaryAction={primaryAction}
      />
    </div>
  )
}
